import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { uniq } from 'lodash';
import {
  QuestionnaireFormProvider,
  useQuestionnaireContext,
  useQuestionnaireFormContext,
} from '@/components/Questionnaire/QuestionnaireProvider';
import removeNullish from '@/helpers/removeNullish';
import HandleWatchers from '../../HandleWatchers';
import areValuesEqual from '../../helpers/areValuesEqual';
import { FormFieldProps } from '../../FormField/types';
import ModalFormContent from './ModalFormContent';
import SaveButton from './SaveButton';
import RemoveButton from './RemoveButton';
import ModalWrapper from './ModalWrapper';
import ModalFormHeader from './ModalFormHeader';
import TransferData from './TransferData';
import ModalFormActions from './ModalFormActions';
import s from './EditListItemModal.module.scss';
import getAllChildFields from '../../helpers/getAllChildFields';

type ThisField =
  & Parameters<typeof ModalFormContent>[0]['field']
  & {
    listItemTemplates?: Array<Parameters<typeof ModalFormContent>[0]['field']>;
  };

type Props = {
  field: ThisField;
  linkSourceFieldKey: string;
  onClose: () => void;
  onEditField: (otherRowIndex: number) => void;
  onRemove?: (thisRowIndex: number) => void;
  onSave: (newValue: { _id: string }) => void;
  rowIndex: number;
  FormFieldComponent: React.FC<FormFieldProps>;
};

const EditListItemModalV2: React.FC<Props> = ({
  field: initialField,
  linkSourceFieldKey,
  onClose,
  onEditField,
  onRemove,
  onSave,
  rowIndex,
  FormFieldComponent,
}) => {
  const {
    fieldsById,
    fieldsByKey,
    formData,
    isEnabled,
  } = useQuestionnaireContext();

  const rowFieldKey = `${linkSourceFieldKey}[${rowIndex}]`;

  const globalFormContext = useQuestionnaireFormContext();
  const modalFormMethods = useForm({
    defaultValues: globalFormContext.formMethods.getValues(),
  });

  const { filterAiAgents, filterAutofills } = useMemo(() => {
    const rowIndexes = new Set<number>([rowIndex]);
    const newFieldsWithAiAgents = new Set<string>();
    const newFieldsWithAutofills = new Set<string>();

    const sourceListField = fieldsByKey[linkSourceFieldKey];
    const allChildFields = getAllChildFields({
      fieldId: sourceListField?.id,
      fieldsById,
    });

    allChildFields.forEach((field) => {
      if (field.autofill?.action === 'ai-agent') {
        newFieldsWithAiAgents.add(field.key);
      } else if (
        field.autofill
        && field.autofill?.action !== 'initial-values'
      ) {
        newFieldsWithAutofills.add(field.key);
      }
    });

    return {
      filterAiAgents: {
        fieldKeys: newFieldsWithAiAgents,
        rowIndexes,
      },
      filterAutofills: {
        fieldKeys: newFieldsWithAutofills,
        rowIndexes,
      },
    };
  }, [fieldsById, fieldsByKey, linkSourceFieldKey, rowIndex]);

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;

  const [field, setField] = useState(initialField);
  const modalSubFields = useMemo(() => (field.listItemTemplates ?? []).map(subField => ({
    ...subField,
    computedKey: `${rowFieldKey}.${subField.key}`,
    computedLinkedFields: fieldsByKey[subField.key]?.linkedFields?.filter(
      f => f.linkedFieldTemplateIndex === rowIndex,
    ),
    computedListItemTemplates: Object.values(fieldsByKey).filter(
      f => f.listFieldTemplateId === subField.id,
    ),
  })), [
    field.listItemTemplates,
    rowIndex,
    rowFieldKey,
    fieldsByKey,
  ]);

  const [fieldsAutofilling, setFieldsAutoFilling] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [referencedListData, setReferencedListData] = useState<any>(null);
  const [currentListSubsection, setCurrentListSubsection] = useState<string | null>(null);

  const listSubsections = useMemo(() => uniq(modalSubFields
    .map((subField) => subField.listSubsection)
    .filter(removeNullish),
  ), [modalSubFields]);

  useEffect(() => {
    setCurrentListSubsection(listSubsections[0] || null);
  }, [listSubsections]);

  const handleOpenModalFromReferencedList: Parameters<typeof ModalFormContent>[0]['onOpenModalFromReferencedList'] = (referencedField) => {
    setField(referencedField);
    if (referencedField.modalIndex !== undefined) {
      onEditField(referencedField.modalIndex);
    }
  };

  const globalFormContextSetValue = globalFormContext.setValue;
  const globalFormContextMergeResponseSources = globalFormContext.mergeResponseSources;
  const globalFormContextMergeAiRequestIds = globalFormContext.mergeAiRequestIds;
  const globalFormContextMergeAutofillResponses = globalFormContext.mergeAutofillStatuses;

  const handleSaveClick = useCallback<Parameters<typeof SaveButton>[0]['onClick']>(({
    newValue,
    responseSources,
    aiRequestIds,
    autofillStatuses,
  }) => {
    if (!isEnabled) {
      return;
    }
    setReferencedListData(null);
    globalFormContextMergeResponseSources(responseSources);
    globalFormContextMergeAiRequestIds(aiRequestIds);
    globalFormContextMergeAutofillResponses(autofillStatuses);

    // setValue must be after responseSources and aiRequestIds, so that watchers get the new responseSources and aiRequestIds
    globalFormContextSetValue({ key: rowFieldKey, value: newValue });
    onSaveRef.current(newValue);
    onCloseRef.current();
  }, [
    rowFieldKey,
    isEnabled,
    globalFormContextSetValue,
    globalFormContextMergeResponseSources,
    globalFormContextMergeAiRequestIds,
  ]);

  const handleRemoveClick = useCallback<Parameters<typeof RemoveButton>[0]['onClick']>(() => {
    if (!isEnabled || !onRemoveRef.current) {
      return;
    }
    if (window.confirm('Are you sure you want to remove this item?')) {
      onRemoveRef.current(rowIndex);
      onCloseRef.current();
    }
  }, [isEnabled, rowIndex]);

  const handleClose = useCallback(() => {
    const hasChanges = !areValuesEqual(
      globalFormContext.formMethods.getValues(rowFieldKey),
      modalFormMethods.getValues(rowFieldKey),
    );
    if (hasChanges) {
      const confirmed = window.confirm('Are you sure you want to close? All changes will be lost.');
      if (!confirmed) return;
    }
    onClose();
    setReferencedListData(null);
  }, [globalFormContext.formMethods, modalFormMethods, onClose, setReferencedListData, rowFieldKey]);

  return (
    <ModalWrapper
      field={field}
      onClose={handleClose}
      rowIndex={rowIndex}
      listSubsections={listSubsections}
      referencedListData={referencedListData}
    >
      <QuestionnaireFormProvider
        formMethods={modalFormMethods}
        id="Modal"
      >
        {isEnabled && (
          <>
            <HandleWatchers
              isAutoSaveEnabled={false}
              isAutofillEnabled={true}
              isAiAgentsEnabled={true}
              isActionsEnabled={true}
              isDynamicOptionsEnabled={true}
              isSectionsConditionEnabled={false}
              formData={formData}
              onStartAgent={setFieldsAutoFilling}
              filterAiAgents={filterAiAgents}
              filterAutofills={filterAutofills}
              currentTableRowKey={rowFieldKey}
            />
            <TransferData
              fromContext={globalFormContext}
            />
          </>
        )}
        <ModalFormHeader
          currentListSubsection={currentListSubsection}
          field={field}
          linkSourceFieldKey={linkSourceFieldKey}
          listSubsections={listSubsections}
          modalSubFields={modalSubFields}
          onSelectSubsection={setCurrentListSubsection}
          rowIndex={rowIndex}
        />
        {isEnabled && (
          <ModalFormActions
            rowIndex={rowIndex}
            linkSourceFieldKey={linkSourceFieldKey}
          />
        )}
        <ModalFormContent
          currentListSubsection={currentListSubsection}
          field={field}
          fieldsAutofilling={fieldsAutofilling}
          FormFieldComponent={FormFieldComponent}
          onUpdateReferencedListData={setReferencedListData}
          linkSourceFieldKey={linkSourceFieldKey}
          listSubsections={listSubsections}
          modalSubFields={modalSubFields}
          onOpenModalFromReferencedList={handleOpenModalFromReferencedList}
          rowIndex={rowIndex}
        />
        {isEnabled && !field.disabled && (
          <div className={s.saveButton}>
            <SaveButton
              onClick={handleSaveClick}
              rowFieldKey={rowFieldKey}
            />
            {onRemove && (
              <RemoveButton
                onClick={handleRemoveClick}
              />
            )}
          </div>
        )}
      </QuestionnaireFormProvider>
    </ModalWrapper>
  );
};

export default EditListItemModalV2;
